// Frontend/src/context/LeaveContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import { leaveService } from '../api/services';
import useEmployeeProfile from '../hooks/useEmployeeProfile';

const LeaveContext = createContext();

export const useLeave = () => {
  const context = useContext(LeaveContext);
  if (!context) {
    throw new Error('useLeave must be used within a LeaveProvider');
  }
  return context;
};

/**
 * Helper function to group leave requests by RequestId
 * API returns one record per day, we need to group them into single requests
 * @param {Array} leaveRequestsArray - Array of leave request records from API
 * @param {Object} leaveTypeUnitMap - Map of LeaveType -> LeaveAmountUnit
 * @param {Object} leaveTypeDescMap - Map of LeaveType -> Description
 */
const groupLeaveRequests = (leaveRequestsArray, leaveTypeUnitMap = {}, leaveTypeDescMap = {}) => {
  if (!leaveRequestsArray || leaveRequestsArray.length === 0) {
    return [];
  }

  const grouped = {};

  leaveRequestsArray.forEach((request) => {
    const requestId = request.RequestId;

    if (!grouped[requestId]) {
      grouped[requestId] = {
        id: requestId,
        RequestId: requestId,
        dataAreaId: request.dataAreaId || 'usmf',
        leaveType: request.LeaveType,
        leaveTypeName: leaveTypeDescMap[request.LeaveType] || request.LeaveType, // Get description from map
        leaveAmountUnit: leaveTypeUnitMap[request.LeaveType] || 'Days', // Get unit from map
        status: request.Status,
        reason: request.Comment || '',
        requestedDate: request.RequestDate,
        personnelNumber: request.PersonnelNumber,
        days: [],
        totalAmount: 0,
        halfDayDefinition: request.HalfDayDefinition,
      };
    }

    // Add this day to the request
    grouped[requestId].days.push({
      date: request.LeaveDate,
      amount: request.Amount || 0,
    });
    grouped[requestId].totalAmount += request.Amount || 0;
  });

  // Convert grouped object to array and calculate start/end dates
  return Object.values(grouped).map((request) => {
    // Sort days by date
    request.days.sort((a, b) => new Date(a.date) - new Date(b.date));

    const startDate = request.days[0].date;
    const endDate = request.days[request.days.length - 1].date;
    const numberOfDays = request.days.length;

    return {
      ...request,
      startDate,
      endDate,
      days: numberOfDays,
      totalHours: request.totalAmount,
      approverName: null, // Not available in API response
      approvedDate: null,
      rejectedDate: null,
      rejectionReason: null,
    };
  });
};

export const LeaveProvider = ({ children }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [myLeaveRequests, setMyLeaveRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cache keys
  const LEAVE_TYPES_CACHE_KEY = 'leave_types_cache';
  const LEAVE_BALANCE_CACHE_KEY = 'leave_balance_cache';
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Get cached data
  const getCachedData = (key) => {
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          return data;
        }
      }
    } catch (error) {
      console.error('Error reading cache:', error);
    }
    return null;
  };

  // Set cached data
  const setCachedData = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error('Error setting cache:', error);
    }
  };

  // Fetch leave types from API using 3-step chain
  // Step 1: Get employee's LeavePlanIds from LeaveEnrollmentsV2
  // Step 2: Get LeaveTypeIds from LeavePlanTiersV2 matching those LeavePlanIds
  // Step 3: Get LeaveTypes filtered by Type matching LeaveTypeIds AND SHPLeaveClassification === "AnnualLeave"
  const fetchLeaveTypes = useCallback(async (personnelNumber) => {
    if (!personnelNumber) {
      console.log('⏳ Personnel number required to fetch leave types');
      setLeaveTypes([]);
      return [];
    }

    try {
      console.log('📋 Fetching leave types for employee:', personnelNumber);

      // Step 1: Get leave enrollments for the employee
      const enrollmentsResponse = await leaveService.getLeaveEnrollments(personnelNumber);
      const enrollments = enrollmentsResponse.data?.value || [];

      // Extract unique LeavePlanIds
      const leavePlanIds = [...new Set(enrollments.map((e) => e.LeavePlanId).filter(Boolean))];
      console.log('📋 Employee LeavePlanIds:', leavePlanIds);

      if (leavePlanIds.length === 0) {
        console.log('⚠️ No leave plans found for employee');
        setLeaveTypes([]);
        return [];
      }

      // Step 2: Get leave plan tiers
      const tiersResponse = await leaveService.getLeavePlanTiers();
      const allTiers = tiersResponse.data?.value || [];

      // Filter tiers matching employee's LeavePlanIds and extract LeaveTypeIds
      const matchingTiers = allTiers.filter((tier) => leavePlanIds.includes(tier.LeavePlanId));
      const leaveTypeIds = [...new Set(matchingTiers.map((t) => t.LeaveTypeId).filter(Boolean))];
      console.log('📋 Matching LeaveTypeIds:', leaveTypeIds);

      if (leaveTypeIds.length === 0) {
        console.log('⚠️ No leave type IDs found from plan tiers');
        setLeaveTypes([]);
        return [];
      }

      // Step 3: Get all leave types
      const leaveTypesResponse = await leaveService.getLeaveTypes();
      const allLeaveTypes = leaveTypesResponse.data?.value || [];

      // Filter by: Type matches LeaveTypeIds AND SHPLeaveClassification === "AnnualLeave"
      const filteredTypes = allLeaveTypes.filter(
        (lt) => leaveTypeIds.includes(lt.Type) && lt.SHPLeaveClassification === 'AnnualLeave'
      );

      // Map to dropdown format
      const mappedTypes = filteredTypes.map((lt) => ({
        id: lt.Type,
        name: lt.Type,
        description: lt.Description,
        dataAreaId: lt.dataAreaId,
        category: lt.Category,
        leaveAmountUnit: lt.LeaveAmountUnit,
        lType: lt.LType,
        classification: lt.SHPLeaveClassification,
      }));

      // Remove duplicates (same Type can exist in multiple dataAreaIds)
      const uniqueTypes = mappedTypes.filter(
        (type, index, self) => index === self.findIndex((t) => t.id === type.id)
      );

      console.log('✅ Annual Leave types loaded:', uniqueTypes.length, 'types');
      setLeaveTypes(uniqueTypes);
      return uniqueTypes;
    } catch (error) {
      console.error('❌ Failed to fetch leave types:', error);
      // Fallback to empty array
      setLeaveTypes([]);
      return [];
    }
  }, []);

  // Fetch leave balance
  const fetchLeaveBalance = useCallback(async (personnelNumber = null) => {
    const cacheKey = personnelNumber
      ? `${LEAVE_BALANCE_CACHE_KEY}_${personnelNumber}`
      : LEAVE_BALANCE_CACHE_KEY;

    const cached = getCachedData(cacheKey);
    if (cached) {
      setLeaveBalance(cached);
      return cached;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('📋 Fetching leave balance for:', personnelNumber || 'current user');

      // Call the API to get leave balances
      const response = personnelNumber
        ? await leaveService.getLeaveBalancesByEmployee(personnelNumber)
        : await leaveService.getEssLeaveBalances();

      const balanceData = response.data.value || [];
      console.log('✅ Leave balance fetched:', balanceData.length, 'records');

      setLeaveBalance(balanceData);
      setCachedData(cacheKey, balanceData);

      return balanceData;
    } catch (err) {
      console.error('❌ Failed to fetch leave balance:', err);
      setError(err.message || 'Failed to fetch leave balance');
      throw err;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch my leave requests
  const fetchMyLeaveRequests = useCallback(async (personnelNumber, filters = {}) => {
    if (!personnelNumber) {
      console.log('⏳ Personnel number required to fetch leave requests');
      return [];
    }

    try {
      setLoading(true);
      setError(null);

      console.log('📋 Fetching leave requests for employee:', personnelNumber);

      // Fetch leave types to get LeaveAmountUnit and Description mapping
      let leaveTypeUnitMap = {};
      let leaveTypeDescMap = {};
      try {
        const leaveTypesResponse = await leaveService.getLeaveTypes();
        const allLeaveTypes = leaveTypesResponse.data?.value || [];
        // Build maps: Type -> LeaveAmountUnit and Type -> Description
        allLeaveTypes.forEach((lt) => {
          leaveTypeUnitMap[lt.Type] = lt.LeaveAmountUnit;
          leaveTypeDescMap[lt.Type] = lt.Description;
        });
        console.log('✅ Leave type maps built:', Object.keys(leaveTypeUnitMap).length, 'types');
      } catch (leaveTypeError) {
        console.warn('⚠️ Failed to fetch leave types for mapping:', leaveTypeError);
      }

      // Build OData filter parameters
      const params = {
        $filter: `PersonnelNumber eq '${personnelNumber}'`,
        $orderby: 'RequestDate desc',
        ...filters,
      };

      const response = await leaveService.getLeaveRequestsByEmployee(personnelNumber, params);
      const leaveRequestsArray = response.data.value || [];

      console.log('✅ Leave requests fetched:', leaveRequestsArray.length, 'records');

      // Group requests by RequestId (since API returns one record per day)
      const groupedRequests = groupLeaveRequests(leaveRequestsArray, leaveTypeUnitMap, leaveTypeDescMap);

      console.log('✅ Grouped into:', groupedRequests.length, 'requests');

      // Fetch LeaveStatus from EssLeaveRequestHeaders for each request
      const requestsWithLeaveStatus = await Promise.all(
        groupedRequests.map(async (request) => {
          try {
            const headerResponse = await leaveService.getEssLeaveRequestHeaderById(
              request.RequestId,
              request.dataAreaId
            );
            const headerData = headerResponse.data;
            return {
              ...request,
              leaveStatus: headerData?.LeaveStatus || request.status,
            };
          } catch (headerError) {
            console.warn(`⚠️ Failed to fetch header for ${request.RequestId}:`, headerError.message);
            return {
              ...request,
              leaveStatus: request.status, // Fallback to status from LeaveRequestsV2
            };
          }
        })
      );

      console.log('✅ Leave status fetched for all requests');

      setMyLeaveRequests(requestsWithLeaveStatus);
      return requestsWithLeaveStatus;
    } catch (err) {
      console.error('❌ Failed to fetch leave requests:', err);
      setError(err.message || 'Failed to fetch leave requests');
      setMyLeaveRequests([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch pending requests (for managers)
  const fetchPendingRequests = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await leaveService.getPendingLeaveRequests(filters);
      setPendingRequests(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch pending requests');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh leave balance
  const refreshLeaveBalance = useCallback(async (personnelNumber = null) => {
    const cacheKey = personnelNumber
      ? `${LEAVE_BALANCE_CACHE_KEY}_${personnelNumber}`
      : LEAVE_BALANCE_CACHE_KEY;
    localStorage.removeItem(cacheKey);
    return fetchLeaveBalance(personnelNumber);
  }, [fetchLeaveBalance]);

  // Refresh leave types from API
  const refreshLeaveTypes = useCallback(async () => {
    console.log('📋 Refreshing leave types from API');
    return fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Don't load initial data automatically - components will call fetch functions when needed

  const value = {
    leaveTypes,
    leaveBalance,
    myLeaveRequests,
    pendingRequests,
    loading,
    error,
    fetchLeaveTypes,
    fetchLeaveBalance,
    fetchMyLeaveRequests,
    fetchPendingRequests,
    refreshLeaveBalance,
    refreshLeaveTypes,
    clearError,
  };

  return (
    <LeaveContext.Provider value={value}>
      {children}
    </LeaveContext.Provider>
  );
};

export default LeaveContext;

